import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Checkbox,
  IconButton,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';

const CategoryTreeNode = ({ node, level, selectedIds, onToggleSelect, expandedIds, onToggleExpand, searchQuery }) => {
  const isExpanded = expandedIds.includes(node.id);
  const isSelected = selectedIds.includes(node.id);
  
  const hasChildren = node.children && node.children.length > 0;
  
  // If there's a search query, we should expand matching nodes automatically or just show them.
  // Actually, we'll let the parent handle the tree filtering.
  
  return (
    <>
      <ListItem 
        sx={{ 
          pl: level * 3, 
          py: 0.5,
          '&:hover': { backgroundColor: '#f1f5f9' },
          borderRadius: 1,
          mb: 0.5
        }}
        disablePadding
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          {hasChildren ? (
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); onToggleExpand(node.id); }}
              sx={{ p: 0.5 }}
            >
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 26 }} /> // Placeholder for alignment
          )}
        </ListItemIcon>
        
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            edge="start"
            checked={isSelected}
            onChange={() => onToggleSelect(node.id)}
            size="small"
            sx={{ p: 0.5, '&.Mui-checked': { color: '#0b3d87' } }}
          />
        </ListItemIcon>
        
        <ListItemText 
          primary={node.name} 
          onClick={() => onToggleSelect(node.id)}
          sx={{ 
            cursor: 'pointer',
            m: 0,
            '& .MuiTypography-root': { 
              fontFamily: '"Rubik", sans-serif', 
              fontSize: '0.85rem',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? '#0b3d87' : 'inherit'
            }
          }}
        />
      </ListItem>
      
      {hasChildren && (
        <Collapse in={isExpanded || searchQuery.length > 0} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <CategoryTreeNode
                key={child.id}
                node={child}
                level={level + 1}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                searchQuery={searchQuery}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

CategoryTreeNode.propTypes = {
  node: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  selectedIds: PropTypes.array.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  expandedIds: PropTypes.array.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired
};

const CategoryTreeFilter = ({ categories, selectedCategoryIds, onChange, label = "Filtra per Categoria" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState([]);

  // Costruisci l'albero dalle categorie flat
  const { tree, flatMap } = useMemo(() => {
    const map = new Map();
    const roots = [];
    
    // Inizializza la mappa
    categories.forEach(cat => {
      map.set(cat.categoryId, {
        id: cat.categoryId,
        name: cat.nome,
        parentId: cat.parentId,
        children: []
      });
    });
    
    // Costruisci l'albero
    categories.forEach(cat => {
      const node = map.get(cat.categoryId);
      if (cat.parentId && map.has(cat.parentId)) {
        map.get(cat.parentId).children.push(node);
      } else {
        roots.push(node);
      }
    });
    
    // Sort children alphabetically
    const sortNodes = (nodes) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach(node => {
        if (node.children.length > 0) sortNodes(node.children);
      });
    };
    sortNodes(roots);
    
    return { tree: roots, flatMap: map };
  }, [categories]);

  // Filtra l'albero in base alla ricerca
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree;
    
    const query = searchQuery.toLowerCase();
    
    const filterNodes = (nodes) => {
      const result = [];
      for (const node of nodes) {
        const isMatch = node.name.toLowerCase().includes(query);
        const filteredChildren = filterNodes(node.children);
        
        if (isMatch || filteredChildren.length > 0) {
          result.push({
            ...node,
            children: filteredChildren
          });
        }
      }
      return result;
    };
    
    return filterNodes(tree);
  }, [tree, searchQuery]);

  const handleToggleSelect = (id) => {
    const currentIndex = selectedCategoryIds.indexOf(id);
    const newSelected = [...selectedCategoryIds];

    if (currentIndex === -1) {
      newSelected.push(id);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    
    onChange(newSelected);
  };

  const handleToggleExpand = (id) => {
    setExpandedIds(prev => 
      prev.includes(id) ? prev.filter(eId => eId !== id) : [...prev, id]
    );
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleDeleteChip = (id, e) => {
    e.stopPropagation();
    handleToggleSelect(id);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'category-tree-popover' : undefined;

  return (
    <>
      <Box onClick={handleOpen} sx={{ width: '100%', cursor: 'pointer' }}>
        <TextField
          fullWidth
          label={label}
          size="small"
          InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s', color: '#64748b' }} />
                </InputAdornment>
              ),
              startAdornment: selectedCategoryIds.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 1, my: 0.5, maxWidth: 'calc(100% - 30px)', overflow: 'hidden' }}>
                  {selectedCategoryIds.slice(0, 1).map(id => {
                    const cat = flatMap.get(id);
                    return (
                      <Chip
                        key={id}
                        label={cat ? cat.name : id}
                        size="small"
                        onDelete={(e) => handleDeleteChip(id, e)}
                        sx={{ height: 24, fontSize: '0.75rem', backgroundColor: '#e2e8f0', color: '#1e293b', fontWeight: 600, '&:hover': { backgroundColor: '#cbd5e1' } }}
                      />
                    );
                  })}
                  {selectedCategoryIds.length > 1 && (
                    <Chip
                      label={`+${selectedCategoryIds.length - 1}`}
                      size="small"
                      sx={{ height: 24, fontSize: '0.75rem', backgroundColor: '#e2e8f0', color: '#1e293b', fontWeight: 600, '&:hover': { backgroundColor: '#cbd5e1' } }}
                    />
                  )}
                </Box>
              ) : null,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              fontSize: "0.85rem",
              transition: "all 0.2s ease-in-out",
              "&:hover": { backgroundColor: "#f1f5f9" },
              "&.Mui-focused": { backgroundColor: "white", "& fieldset": { borderColor: "#0b3d87 !important" } },
              "& fieldset": { borderColor: "#e2e8f0 !important" },
              "& .MuiInputBase-input": {
                display: selectedCategoryIds.length > 0 ? 'none' : 'block'
              }
            }
          }}
        />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { 
              width: anchorEl ? anchorEl.clientWidth : 300,
              maxHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              mt: 1
            }
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca categoria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '12px', backgroundColor: '#f8fafc' }
              }
            }}
          />
        </Box>
        
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
          {filteredTree.length > 0 ? (
            <List disablePadding>
              {filteredTree.map(node => (
                <CategoryTreeNode
                  key={node.id}
                  node={node}
                  level={0}
                  selectedIds={selectedCategoryIds}
                  onToggleSelect={handleToggleSelect}
                  expandedIds={expandedIds}
                  onToggleExpand={handleToggleExpand}
                  searchQuery={searchQuery}
                />
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: '#64748b' }}>
              Nessuna categoria trovata
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

CategoryTreeFilter.propTypes = {
  categories: PropTypes.array.isRequired,
  selectedCategoryIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string
};

export default CategoryTreeFilter;
 